import React, { FC, useState, useRef } from 'react';

import { ReactComponent as AddReaction } from 'assets/icons/AddReaction.svg';
import { CardSchema, ECardExtractionOptions, UserObjectType } from 'constants/graphqlTypes';
import { getUserInfo } from 'components/Profile/ProfileTop/helpers';
import { getCardTagCaption } from 'components/Card/helpers/helpers';
import { isVideoCard } from 'helpers/isVideoCard';
import getStyles from 'helpers/getStyles';
import UILink from 'components/UI/Link';
import Avatar from 'components/UI/Avatar/Avatar';
import ReactionMenu from 'components/Reactions/ReactionMenu/ReactionMenu';
import CardFooterMenu from 'components/UI/CardFooterMenu';
import { StandardCard } from 'components/CardV2';
import { ROUTE_USER_PROFILE } from 'routes';
import { COLORS } from 'styles/constants';

import {
  StyledSparkResponseCard,
  StyledUserSection,
  StyledThoughtRow,
  StyledThoughtText,
  StyledReactionButton,
  StyledCardSection,
  StyledCardFooter,
} from './styled';

interface ISparkResponseCardProps {
  card: CardSchema;
  isDark?: boolean;
  sparkTitle?: string;
  isOwner?: boolean;
  footerIsDarkMode?: boolean;
}

const SparkResponseCard: FC<ISparkResponseCardProps> = ({
  card,
  isDark = false,
  sparkTitle,
  isOwner = false,
  footerIsDarkMode = false,
}) => {
  const {
    user,
    cardStyle,
    extraData,
    pickedByUsers,
    externalSources,
    linkImageSets,
    mediaTags,
    extractionStatus,
  } = card;
  const [isReactionMenuOpen, setIsReactionMenuOpen] = useState(false);
  const [reactionMenuCoords, setReactionMenuCoords] = useState<{ x: number; y: number } | undefined>(undefined);
  const reactionButtonRef = useRef<HTMLButtonElement>(null);

  // Get card styles (background color)
  const { bgColor } = getStyles(cardStyle);

  // Image: try thumbnailImageSet, then linkImageSets
  const thumbnailUrl = cardStyle?.thumbnailImageSet?.images?.[0]?.url;
  const linkImageUrl = linkImageSets?.items?.[0]?.images?.[0]?.url;
  const imageUrl = thumbnailUrl || linkImageUrl || null;

  // Get media type tag for eyebrow (e.g., "Article", "Video")
  const tagName = mediaTags?.items?.[0]?.name;
  const isVideo = isVideoCard(card);
  const isTweet = tagName === 'tweets';
  const tag = !tagName && extractionStatus !== ECardExtractionOptions.Complete ? 'Loading' : tagName;
  const mediaTypeLabel = getCardTagCaption({ isVideo, isTweet, tag });

  // External source URL
  const externalLink = externalSources?.items?.[0]?.sourceUrl ?? null;

  // Try to get user from multiple sources:
  // 1. Card's user (original creator)
  // 2. extraData (user who commented/added to spark)
  // 3. pickedByUsers (users who picked this card for this spark)
  const extraDataUser = extraData?.items?.[0]?.user as UserObjectType | undefined;
  const pickedByUser = pickedByUsers?.items?.[0] as UserObjectType | undefined;
  const displayUser = user || extraDataUser || pickedByUser;

  const { username } = displayUser ? getUserInfo(displayUser) : { username: '' };

  // Get thought from extraData comment only - not card.comments
  // extraData returns spark-specific comments (from users who added the card to THIS spark)
  // card.comments returns ALL comments on the card, which would be the original adder's comment
  // extraData can contain CardCommentSchema (has text) or CardPickSchema (no text)
  const extraDataItem = extraData?.items?.[0] as { text?: string } | undefined;
  const extraDataThought = extraDataItem?.text;

  // Only use the extraData thought - DO NOT fall back to card.comments
  // If no spark-specific thought exists, show "Added to {title}" as fallback
  const rawThought = extraDataThought;
  const thought = rawThought || (sparkTitle ? `Added to ${sparkTitle}` : '');

  // Determine footer font color based on footerIsDarkMode
  const footerFontColor = footerIsDarkMode ? COLORS.white[100] : COLORS.brown.dark[100];

  const openReactionMenu = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (reactionButtonRef.current) {
      const rect = reactionButtonRef.current.getBoundingClientRect();
      setReactionMenuCoords({
        x: rect.left + rect.width / 2,
        y: rect.top - 10,
      });
    }
    setIsReactionMenuOpen(true);
  };

  const closeReactionMenu = () => setIsReactionMenuOpen(false);

  // Only skip rendering if we have absolutely no user info
  if (!displayUser) return null;

  return (
    <StyledSparkResponseCard isDark={isDark}>
      {/* User Section - Small avatar + thought + reaction button */}
      <StyledUserSection isOwner={isOwner}>
        <UILink route={ROUTE_USER_PROFILE} params={{ profileName: username }}>
          <Avatar user={displayUser} dimension={20} />
        </UILink>
        <StyledThoughtRow>
          <StyledThoughtText isDark={isDark}>{rawThought ? `"${rawThought}"` : thought}</StyledThoughtText>
          {!isOwner && (
            <>
              <StyledReactionButton ref={reactionButtonRef} onClick={openReactionMenu} aria-label="Add Reaction">
                <AddReaction style={{ color: isDark ? '#fff' : '#2D2B28' }} />
              </StyledReactionButton>
              {isReactionMenuOpen && (
                <ReactionMenu
                  isOpen={isReactionMenuOpen}
                  anchorPoint={reactionMenuCoords ?? { x: 0, y: 0 }}
                  onClose={closeReactionMenu}
                  onSend={() => {}}
                  onEmoji={() => {}}
                />
              )}
            </>
          )}
        </StyledThoughtRow>
      </StyledUserSection>

      {/* Card Section — uses CardV2 StandardCard for consistent styling */}
      <StyledCardSection>
        <StandardCard
          title={card.title ?? ''}
          imageUrl={imageUrl}
          backgroundColor={bgColor}
          externalLink={externalLink}
          eyebrow={mediaTypeLabel || null}
        />
        <StyledCardFooter>
          <CardFooterMenu card={card} color={footerFontColor} isMyProfile={false} />
        </StyledCardFooter>
      </StyledCardSection>
    </StyledSparkResponseCard>
  );
};

export default SparkResponseCard;
